import { ShieldCheck, BadgeCheck, TrendingUp, Globe2, Users, ClipboardList, Lightbulb, Flag } from "lucide-react";
import { useLang } from "@/lib/i18n";

const fmtRp = (n) => `Rp ${Number(n || 0).toLocaleString("id-ID")}`;
const EVIDENCE_STYLE = {
  "Organizer Reviewed": "bg-emerald-100 text-emerald-800",
  "Attendance Recorded via PIN/QR": "bg-teal-100 text-teal-800",
  "Evidence Attached": "bg-sky-100 text-sky-800",
  "Self-Reported": "bg-amber-100 text-amber-800",
};

function PageShell({ page, title, children }) {
  const { t } = useLang();
  return (
    <section className="report-page px-8 py-7 border-b-4 border-dashed border-slate-200 last:border-0">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-heading font-black text-lg text-slate-900">{title}</h3>
        <span className="text-[10px] font-black text-slate-400 uppercase">{t("rp_page")} {page}/3</span>
      </div>
      {children}
    </section>
  );
}

export default function ImpactReportContent({ stats }) {
  const { t } = useLang();
  const resolution = Math.round((stats.issue_resolution.resolved / stats.issue_resolution.reported) * 100);
  const va = stats.volunteer_analytics;

  return (
    <div id="executive-impact-report" className="report-print bg-white">
      <div className="bg-slate-900 px-8 py-6 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <span className="bg-emerald-600 text-white rounded-lg p-1"><ShieldCheck className="w-4 h-4" /></span>
            <span className="font-heading font-black text-white">Bergerak<span className="text-emerald-400">Nyata</span></span>
          </div>
          <h2 className="font-heading font-black text-xl text-white mt-2">Executive Impact Report</h2>
          <p className="text-xs text-emerald-300 font-bold uppercase tracking-wider">{t("rp_subtitle")}</p>
        </div>
        <span className="text-[10px] font-bold px-3 py-1.5 rounded-full bg-amber-400/20 text-amber-300 border border-amber-400/40">DEMO MODE — SAMPLE DATA</span>
      </div>

      <PageShell page={1} title={t("rp_title1")}>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
          {[
            { label: t("rp_stat_vol"), val: stats.total_volunteers.toLocaleString("id-ID") },
            { label: t("rp_stat_act"), val: stats.completed_projects },
            { label: t("rp_stat_att"), val: stats.verified_attendance.toLocaleString("id-ID") },
            { label: t("rp_stat_hours"), val: stats.total_hours.toLocaleString("id-ID") },
            { label: t("rp_stat_cities"), val: stats.cities_reached },
          ].map((s) => (
            <div key={s.label} className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-center">
              <p className="font-heading font-black text-xl text-slate-900">{s.val}</p>
              <p className="text-[9px] font-bold text-slate-500 uppercase tracking-wide mt-1 leading-tight">{s.label}</p>
            </div>
          ))}
        </div>
        <div data-testid="economic-value-section" className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-5">
          <p className="text-xs font-black text-emerald-700 uppercase tracking-wider flex items-center gap-1.5">
            <TrendingUp className="w-4 h-4" /> {t("rp_econ")}
          </p>
          <p className="font-heading font-black text-3xl text-emerald-800 mt-1">{fmtRp(stats.economic_value)}</p>
          <p className="text-sm text-emerald-700 mt-1 font-semibold">
            Formula: {stats.total_hours.toLocaleString("id-ID")} {t("rp_formula_mid")} {fmtRp(stats.hourly_value)}{t("rp_formula_end")}
          </p>
        </div>
        <p className="text-xs text-slate-400 mt-4 flex items-center gap-1.5">
          <BadgeCheck className="w-4 h-4 text-emerald-600" />
          {t("rp_source")}
        </p>
      </PageShell>

      <PageShell page={2} title={t("rp_title2")}>
        <div className="mb-5" data-testid="sdg-section">
          <p className="text-xs font-black text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-1.5"><Globe2 className="w-4 h-4" /> {t("rp_sdg_head")}</p>
          {stats.sdgs.map((s) => (
            <div key={s.code} className="flex items-start gap-3 mb-3">
              <span className="w-14 shrink-0 text-center text-[9px] font-black px-1.5 py-1.5 rounded-lg bg-slate-900 text-white">{s.code}</span>
              <div className="flex-1">
                <div className="flex justify-between text-xs font-bold text-slate-700"><span>{s.name}</span><span>{s.pct}%</span></div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden mt-1">
                  <div className="h-full bg-emerald-600 rounded-full" style={{ width: `${s.pct}%` }} />
                </div>
                <p className="text-[10px] text-slate-500 mt-0.5 italic">{s.rationale}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mb-5" data-testid="matrix-section">
          <p className="text-xs font-black text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><ClipboardList className="w-4 h-4" /> {t("rp_matrix_head")}</p>
          <div className="border border-slate-200 rounded-xl overflow-hidden">
            <div className="grid grid-cols-12 bg-slate-100 text-[10px] font-black text-slate-600 uppercase px-3 py-2">
              <span className="col-span-4">{t("mx_activity")}</span><span className="col-span-3">{t("mx_output")}</span><span className="col-span-3">{t("mx_outcome")}</span><span className="col-span-2">{t("mx_evidence_status")}</span>
            </div>
            {stats.project_records.map((r, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2.5 border-t border-slate-100 text-xs items-center">
                <span className="col-span-4 font-bold text-slate-800">{r.activity}</span>
                <span className="col-span-3 text-slate-600">{r.output}</span>
                <span className="col-span-3 text-emerald-700 font-semibold">{r.outcome}</span>
                <span className={`col-span-2 text-[9px] font-black px-1.5 py-1 rounded-full text-center ${EVIDENCE_STYLE[r.evidence] || "bg-slate-100"}`}>{r.evidence}</span>
              </div>
            ))}
          </div>
        </div>

        <div data-testid="volunteer-analytics-section">
          <p className="text-xs font-black text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><Users className="w-4 h-4" /> {t("rp_va_head")}</p>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-center">
              <p className="font-heading font-black text-lg text-slate-900">{va.returning_pct}% / {va.new_pct}%</p>
              <p className="text-[9px] font-bold text-slate-500 uppercase">{t("rp_va_return")}</p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-center">
              <p className="font-heading font-black text-lg text-emerald-700">{va.attendance_rate}%</p>
              <p className="text-[9px] font-bold text-slate-500 uppercase">{t("rp_va_att")}</p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
              {va.skills_distribution.slice(0, 3).map((s) => (
                <div key={s.name} className="flex items-center gap-2 mb-1">
                  <span className="text-[9px] font-bold text-slate-600 w-24 truncate">{s.name}</span>
                  <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden"><div className="h-full bg-teal-600" style={{ width: `${s.pct}%` }} /></div>
                  <span className="text-[9px] font-black">{s.pct}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </PageShell>

      <PageShell page={3} title={t("rp_title3")}>
        <div className="grid md:grid-cols-3 gap-4" data-testid="evaluation-section">
          <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
            <p className="text-xs font-black text-rose-700 uppercase mb-2 flex items-center gap-1"><Flag className="w-3.5 h-3.5" /> {t("rp_challenges")}</p>
            <ul className="space-y-1.5">{stats.evaluation.challenges.map((c, i) => <li key={i} className="text-xs text-rose-800">• {c}</li>)}</ul>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-xs font-black text-amber-700 uppercase mb-2 flex items-center gap-1"><Lightbulb className="w-3.5 h-3.5" /> {t("rp_lessons")}</p>
            <ul className="space-y-1.5">{stats.evaluation.lessons.map((c, i) => <li key={i} className="text-xs text-amber-800">• {c}</li>)}</ul>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
            <p className="text-xs font-black text-emerald-700 uppercase mb-2 flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" /> {t("rp_next")}</p>
            <ul className="space-y-1.5">{stats.evaluation.next_steps.map((c, i) => <li key={i} className="text-xs text-emerald-800">• {c}</li>)}</ul>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-4">
          <div className="bg-slate-900 rounded-xl p-4 text-center">
            <p className="font-heading font-black text-3xl text-white">{resolution}%</p>
            <p className="text-[10px] text-slate-400 mt-1">{t("rp_resolution")} ({stats.issue_resolution.resolved}/{stats.issue_resolution.reported})</p>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-center justify-center">
            <p className="text-xs text-slate-500 text-center flex items-center gap-1.5">
              <BadgeCheck className="w-4 h-4 text-emerald-600 shrink-0" />
              {t("rp_footer")}
            </p>
          </div>
        </div>
      </PageShell>
    </div>
  );
}
